import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MapPin, ExternalLink, ChevronRight, BadgeCheck, Clock, Heart, X, ZoomIn, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import BottomNav from '../components/BottomNav';
import ShareButton from '../components/ShareButton';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=600';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { location: userLocation } = useLocation();
  const { isAuthenticated } = useAuth();
  const { t } = useT();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const fetchProduct = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      const response = await axios.get(`${API}/products/${id}?${params.toString()}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  }, [id, userLocation]);

  const trackView = useCallback(async () => {
    try {
      await axios.post(`${API}/products/${id}/view`);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  }, [id]);

  const checkFavorite = useCallback(async () => {
    if (!product?.negozio_id) return;
    try {
      const response = await axios.get(`${API}/favorites/check/${product.negozio_id}`, { withCredentials: true });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.debug('checkFavorite skipped (likely guest):', error.message);
    }
  }, [product?.negozio_id]);

  useEffect(() => {
    fetchProduct();
    trackView();
  }, [fetchProduct, trackView]);

  useEffect(() => {
    if (product?.negozio_id) {
      checkFavorite();
    }
  }, [product?.negozio_id, checkFavorite]);

  // Close lightbox on Escape
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen]);

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/negoziante');
      return;
    }
    if (!product?.negozio_id) return;

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${product.negozio_id}`, { withCredentials: true });
        setIsFavorite(false);
      } else {
        await axios.post(`${API}/favorites/${product.negozio_id}`, {}, { withCredentials: true });
        setIsFavorite(true);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleGoToShop = async () => {
    if (!product) return;

    try {
      await axios.post(`${API}/products/${id}/click`);
    } catch (error) {
      console.error('Error tracking click:', error);
    }

    // For events, prefer the event's own location (evento_lat/lng) over the shop's.
    const isEvent = product.tipo === 'event';
    const destLat = isEvent ? (product.evento_lat || product.negozio_lat) : product.negozio_lat;
    const destLng = isEvent ? (product.evento_lng || product.negozio_lng) : product.negozio_lng;

    if (destLat && destLng) {
      const dest = `${destLat},${destLng}`;
      let url;
      if (userLocation?.lat && userLocation?.lng) {
        const origin = `${userLocation.lat},${userLocation.lng}`;
        url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=driving`;
      } else {
        url = `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const isEventProduct = product?.tipo === 'event';
  const ctaLabel = isEventProduct ? t('product.go_to_event') : t('product.go_to_shop');

  const handleTagClick = (tag) => {
    navigate(`/search?tag=${encodeURIComponent(tag)}`);
  };

  const handleShopClick = () => {
    if (product?.negozio_id) {
      navigate(`/negozio/${product.negozio_id}`);
    }
  };

  const formatDistance = (km) => {
    if (km === null || km === undefined) return null;
    if (km < 1) {
      return `${Math.round(km * 1000)} m`;
    }
    return `${km.toFixed(1)} km`;
  };

  const calculateDiscountedPrice = () => {
    if (product?.prezzo && product?.sconto) {
      return product.prezzo * (1 - product.sconto / 100);
    }
    return null;
  };

  const getAvailabilityInfo = () => {
    if (product?.disponibilita !== 'disponibile') {
      return { text: t('product.sold_out'), color: 'bg-red-100 text-red-700' };
    }
    if (product?.negozio_is_open === true) {
      return { text: t('product.available_now'), color: 'bg-green-100 text-green-700' };
    }
    if (product?.negozio_is_open === false && product?.negozio_next_opening) {
      const tx = product.negozio_next_opening || '';
      const label = tx.toLowerCase().startsWith('chiuso') ? tx : t('product.opens_at', { time: tx });
      return { text: label, color: 'bg-amber-100 text-amber-700' };
    }
    return { text: t('product.available'), color: 'bg-green-100 text-green-700' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <span className="text-6xl mb-4">😕</span>
        <h2 className="font-outfit font-semibold text-xl text-slate-700 mb-2">{t('product.not_found')}</h2>
        <Button onClick={() => navigate(-1)} variant="outline">
          {t('common.goBack')}
        </Button>
      </div>
    );
  }

  const images = product.immagini?.length > 0 ? product.immagini : [DEFAULT_IMAGE];
  const discountedPrice = calculateDiscountedPrice();
  const availability = getAvailabilityInfo();

  return (
    <div className="min-h-screen bg-white pb-32 lg:pb-24" data-testid="product-detail-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-100">
        <div className="flex items-center justify-between px-4 py-3 max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 hover:bg-slate-100 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-6 h-6 text-slate-700" />
          </button>
          <h1 className="font-outfit font-semibold text-lg text-slate-800 truncate max-w-[180px] lg:max-w-md">
            {product.nome}
          </h1>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleFavorite}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              data-testid="favorite-button"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-slate-400'}`} />
            </button>
            <ShareButton
              title={product.nome}
              testId="product-share"
              variant="icon"
            />
          </div>
        </div>
      </header>

      {/* Main content
          MOBILE: image first (full width), info below, sticky bottom CTA.
          DESKTOP (lg ≥ 1024px): 3-column grid → image 1/3 (sticky), info+CTA 2/3.
      */}
      <main className="max-w-7xl mx-auto lg:px-6 lg:py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Image column */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-20">
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="relative w-full bg-slate-100 lg:rounded-2xl lg:overflow-hidden block group focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label={t('product.view_image')}
                data-testid="product-image-zoom-trigger"
              >
                <div className="aspect-square">
                  <img
                    src={images[currentImageIndex]}
                    alt={product.nome}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    onError={(e) => {
                      e.target.src = DEFAULT_IMAGE;
                    }}
                    data-testid="product-main-image"
                  />
                </div>

                {/* Zoom hint icon (desktop) */}
                <span
                  className="absolute bottom-3 right-3 hidden lg:flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-hidden="true"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                  {t('product.view_image')}
                </span>

                {/* Discount badge */}
                {product.sconto && (
                  <span className="absolute top-4 right-4 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
                    -{product.sconto}%
                  </span>
                )}

                {/* Image indicators */}
                {images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((src, idx) => (
                      <button
                        key={`${src}-${idx}`}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(idx);
                        }}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentImageIndex ? 'bg-blue-600' : 'bg-white/60'
                        }`}
                        data-testid={`image-indicator-${idx}`}
                        aria-label={`Image ${idx + 1}`}
                      />
                    ))}
                  </div>
                )}
              </button>

              {/* Thumbnail strip (desktop, only if multiple images) */}
              {images.length > 1 && (
                <div className="hidden lg:flex gap-2 mt-3" data-testid="thumbnail-strip">
                  {images.map((src, idx) => (
                    <button
                      key={`thumb-${src}-${idx}`}
                      type="button"
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${
                        idx === currentImageIndex
                          ? 'border-blue-600 ring-2 ring-blue-200'
                          : 'border-slate-200 hover:border-slate-400'
                      }`}
                      data-testid={`thumbnail-${idx}`}
                      aria-label={`Image ${idx + 1}`}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info + CTA column */}
          <div className="lg:col-span-2">
            <div className="p-4 lg:p-0 space-y-4">
              {/* Name */}
              <h2 className="font-outfit font-bold text-2xl lg:text-3xl text-slate-800" data-testid="product-title">
                {product.nome}
              </h2>

              {/* Price */}
              <div className="flex items-baseline gap-3">
                {discountedPrice ? (
                  <>
                    <span className="text-3xl font-bold text-orange-500" data-testid="product-discounted-price">
                      {discountedPrice.toFixed(2)} €
                    </span>
                    <span className="text-xl text-slate-400 line-through" data-testid="product-original-price">
                      {product.prezzo.toFixed(2)} €
                    </span>
                  </>
                ) : product.prezzo ? (
                  <span className="text-3xl font-bold text-slate-800" data-testid="product-price">
                    {product.prezzo.toFixed(2)} €
                  </span>
                ) : (
                  <span className="text-lg text-slate-500">{t('product.price_in_store')}</span>
                )}
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${availability.color}`}>
                  {product.negozio_is_open === true && <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />}
                  {availability.text}
                </span>
                {product.distanza !== null && product.distanza !== undefined && (
                  <Badge variant="secondary" className="flex items-center gap-1 text-sm">
                    <MapPin className="w-3.5 h-3.5" />
                    {formatDistance(product.distanza)}
                  </Badge>
                )}
              </div>

              {/* Description */}
              {product.descrizione && (
                <div className="pt-4 border-t border-slate-100">
                  <h3 className="font-outfit font-semibold text-slate-700 mb-2">{t('product.description')}</h3>
                  <p className="text-slate-600 leading-relaxed" data-testid="product-description">
                    {product.descrizione}
                  </p>
                </div>
              )}

              {/* Clickable Tags */}
              {product.tag?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {product.tag.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => handleTagClick(tag)}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-full transition-colors"
                      data-testid={`tag-${tag}`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              )}

              {/* Shop info */}
              <div className="pt-4 border-t border-slate-100">
                <h3 className="font-outfit font-semibold text-slate-700 mb-3">{t('product.available_at_shop')}</h3>

                <div
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={handleShopClick}
                  data-testid="shop-card"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      {product.negozio_logo ? (
                        <img src={product.negozio_logo} alt="" className="w-12 h-12 rounded-xl object-cover" />
                      ) : (
                        <MapPin className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-800">{product.negozio_nome}</h4>
                        {product.negozio_verified && (
                          <BadgeCheck className="w-4 h-4 text-blue-500" />
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{product.negozio_indirizzo}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {product.distanza !== null && product.distanza !== undefined && (
                          <span className="text-sm text-slate-600 flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {formatDistance(product.distanza)}
                          </span>
                        )}
                        {product.negozio_is_open !== null && (
                          <span className={`text-sm font-medium flex items-center gap-1 ${
                            product.negozio_is_open ? 'text-green-600' : 'text-amber-600'
                          }`}>
                            <Clock className="w-3.5 h-3.5" />
                            {product.negozio_is_open ? t('product.open') : t('product.closed')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
              </div>

              {/* Desktop CTA — inline in the column, not floating */}
              <div className="hidden lg:block pt-4 space-y-2">
                {product.tipo === 'service' && product.mostra_telefono && product.negozio_telefono && (
                  <a
                    href={`tel:${product.negozio_telefono}`}
                    className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-xl text-base font-semibold transition-colors"
                    data-testid="call-to-book-button-desktop"
                  >
                    <Phone className="w-5 h-5" /> Chiama per prenotare
                  </a>
                )}
                <Button
                  onClick={handleGoToShop}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 rounded-xl text-lg font-semibold flex items-center justify-center gap-2"
                  data-testid="go-to-shop-button-desktop"
                >
                  <MapPin className="w-5 h-5" />
                  {ctaLabel}
                  <ExternalLink className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Fixed bottom CTA — MOBILE ONLY */}
      <div
        className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-slate-100 z-40 lg:hidden space-y-2"
        data-testid="mobile-cta-bar"
      >
        {product.tipo === 'service' && product.mostra_telefono && product.negozio_telefono && (
          <a
            href={`tel:${product.negozio_telefono}`}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl text-base font-semibold transition-colors"
            data-testid="call-to-book-button"
          >
            <Phone className="w-5 h-5" /> Chiama per prenotare
          </a>
        )}
        <Button
          onClick={handleGoToShop}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl text-lg font-semibold flex items-center justify-center gap-2"
          data-testid="go-to-shop-button"
        >
          <MapPin className="w-5 h-5" />
          {ctaLabel}
          <ExternalLink className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Lightbox / fullscreen image viewer */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={product.nome}
          data-testid="product-lightbox"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
            aria-label={t('common.close')}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>

          <img
            src={images[currentImageIndex]}
            alt={product.nome}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              e.target.src = DEFAULT_IMAGE;
            }}
            data-testid="lightbox-image"
          />

          {/* Thumbnail strip in lightbox (if multiple) */}
          {images.length > 1 && (
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 bg-black/40 px-3 py-2 rounded-full"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((src, idx) => (
                <button
                  key={`lb-thumb-${src}-${idx}`}
                  type="button"
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    idx === currentImageIndex ? 'bg-white' : 'bg-white/40 hover:bg-white/70'
                  }`}
                  data-testid={`lightbox-indicator-${idx}`}
                  aria-label={`Image ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <BottomNav />
    </div>
  );
}
